import { useNavigate } from 'react-router-dom';

import { formatMoney } from '../../../utils/formatters';
import type { AttentionItem } from '../attention';
import type { HeadlineKpis, Kpi } from '../../../types/dashboard';

interface Props {
  kpi: Kpi;
  headline: HeadlineKpis;
  attention: AttentionItem[];
  claimsCount: number;
}

type DeltaTone = 'info' | 'success' | 'warning' | 'error';

const DELTA_TONES: Record<DeltaTone, { color: string; bg: string }> = {
  info: { color: '#175CD3', bg: '#EAF1FE' },
  success: { color: '#2E6B3E', bg: '#EAF2EA' },
  warning: { color: '#B45309', bg: '#FDF0D9' },
  error: { color: '#B42318', bg: '#FEF3F2' },
};

function KpiCard({
  label,
  value,
  unit,
  sub,
  delta,
  deltaTone,
  half,
  onClick,
}: {
  label: string;
  value: string;
  unit?: string;
  sub: string;
  delta?: string;
  deltaTone?: DeltaTone;
  half?: boolean;
  onClick: () => void;
}) {
  return (
    <div className={`dash-card dash-kpi${half ? ' is-half' : ''}`} onClick={onClick}>
      <div className="dash-kpi-head">
        <span className="dash-section-label">{label}</span>
        {delta && deltaTone && (
          <span
            className="dash-kpi-delta"
            style={{ color: DELTA_TONES[deltaTone].color, background: DELTA_TONES[deltaTone].bg }}
          >
            {delta}
          </span>
        )}
      </div>
      <div
        className="dash-kpi-value"
        // На півкартці бейдж прихований (не влазить), тож тон несе сама цифра.
        style={half && deltaTone ? { color: DELTA_TONES[deltaTone].color } : undefined}
      >
        {value}
        {unit && <span className="dash-kpi-unit">{unit}</span>}
      </div>
      <div className="dash-kpi-sub">{sub}</div>
    </div>
  );
}

export function KpiCards({ kpi, headline, attention, claimsCount }: Props) {
  const navigate = useNavigate();
  const exp = headline.expected_supplies;
  const expM3 = parseFloat(exp.volume_m3);
  const expValue = parseFloat(exp.value_uah);
  const round = headline.expected_round_supplies;
  const roundM3 = parseFloat(round.volume_m3);
  const roundValue = parseFloat(round.value_uah);
  const ordersValue = parseFloat(kpi.orders.total_value_uah);

  const suppliesSub =
    [
      exp.docs_count > 0 ? `${exp.docs_count} заявки` : null,
      expValue > 0 ? formatMoney(expValue) : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'немає відкритих заявок';

  const roundSub =
    [
      round.docs_count > 0 ? `${round.docs_count} заявки` : null,
      roundValue > 0 ? formatMoney(roundValue) : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'немає відкритих заявок';

  const ordersSub =
    [
      ordersValue > 0 ? formatMoney(ordersValue) : null,
      kpi.orders.nearest_deadline_days !== null
        ? `найближчий дедлайн ${kpi.orders.nearest_deadline_days} дн.`
        : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'активних замовлень немає';

  const scrollToAttention = () => {
    document.getElementById('attention-block')?.scrollIntoView({ block: 'center' });
  };

  return (
    <div className="dash-kpi-grid">
      <KpiCard
        label="Очікувані поставки дошки"
        value={expM3.toLocaleString('uk-UA', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })}
        unit="м³"
        sub={suppliesSub}
        onClick={() => navigate('/purchases/raw')}
      />
      <KpiCard
        label="Очікувані поставки кругляка"
        value={roundM3.toLocaleString('uk-UA', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })}
        unit="м³"
        sub={roundSub}
        onClick={() => navigate('/purchases/round')}
      />
      <KpiCard
        label="Активні замовлення"
        value={String(kpi.orders.active_count)}
        sub={ordersSub}
        onClick={() => navigate('/orders')}
      />
      <KpiCard
        half
        label="Рекламації"
        value={String(claimsCount)}
        sub={claimsCount > 0 ? 'чекають відповіді' : 'нових немає'}
        delta={claimsCount > 0 ? 'на розгляді' : undefined}
        deltaTone="warning"
        onClick={() => navigate('/claims')}
      />
      <KpiCard
        half
        label="Потребує уваги"
        value={String(attention.length)}
        sub={attention.length > 0 ? 'потребують розгляду' : 'зауважень немає'}
        delta={attention.length > 0 ? 'Розглянути' : 'Все гаразд'}
        deltaTone={attention.length > 0 ? 'error' : 'success'}
        onClick={scrollToAttention}
      />
    </div>
  );
}
